import axiosInstance from '@/core/api'
import {
	AdminApproveWithdrawRequest,
	CancelWithdrawTransactionRequest,
	CancelSmsAutoLogRequest,
	CreateManualDepositTransactionRequest,
	CreateManualWithdrawTransactionRequest,
	CreateWithdrawTransactionRequest,
	DepositTransactionResponse,
	ForceCancelDepositTransactionRequest,
	ForceCompleteDepositRequest,
	ForceCompleteWithdrawRequest,
	GetDepositTransactionsByIdResponse,
	GetWithdrawalTransactionsByIdResponse,
	GetDepositTransactionsParams,
	GetWithdrawalTransactionsParams,
	WithdrawTransactionResponse,
	GetTrueWalletLogsParams,
	PaginatedTrueWalletTransactionLogsResponse,
	GetSmsAutoLogsParams,
	PaginatedSmsAutoTransactionLogsResponse,
	RematchSmsAutoLogRequest,
	SearchUsersForRematchParams,
	SearchUsersForRematchResponse,
	SmsAutoTransactionLogResponse,
	TrueMoneyDepositsResponse,
	GetTrueMoneyDepositsParams,
	TransactionStatisticsResponse,
	GetTransactionStatisticsParams,
	RefundWithdrawTransactionRequest,
} from '../../types'

class BankTransactionService {
	async getDepositTransactions(params: GetDepositTransactionsParams) {
		const response = await axiosInstance.get<DepositTransactionResponse>(
			'admin/bank-transactions/deposits',
			{ params },
		)
		return response.data
	}

	async getWithdrawalTransactions(params: GetWithdrawalTransactionsParams) {
		const response = await axiosInstance.get<WithdrawTransactionResponse>(
			'/admin/bank-transactions/withdrawals',
			{ params },
		)
		return response.data
	}

	async forceCompleteDeposit(id: string, data: ForceCompleteDepositRequest) {
		const response = await axiosInstance.patch(
			`/admin/bank-transactions/deposits/${id}/force-complete`,
			data,
		)
		return response.data
	}

	async manualBankWithdraw(id: string) {
		const response = await axiosInstance.patch<DepositTransactionResponse>(
			`/admin/bank-transactions/withdrawals/${id}/manual`,
		)
		return response.data
	}

	async forceCompleteWithdraw(id: string, data?: ForceCompleteWithdrawRequest) {
		const response = await axiosInstance.patch(
			`/admin/bank-transactions/withdrawals/${id}/force-complete`,
			data,
		)
		return response.data
	}

	async cancelWithdraw(id: string, data?: CancelWithdrawTransactionRequest) {
		const response = await axiosInstance.patch(
			`/admin/bank-transactions/withdrawals/${id}/cancel`,
			data,
		)
		return response.data
	}

	async createManualBankDeposit(data: CreateManualDepositTransactionRequest) {
		const response = await axiosInstance.post(
			`/admin/bank-transactions/deposits/manual`,
			data,
		)

		return response.data
	}

	async forceCancelBankDeposit(
		id: string,
		data?: ForceCancelDepositTransactionRequest,
	) {
		const response = await axiosInstance.patch<void>(
			`/admin/bank-transactions/deposits/${id}/cancel`,
			data,
		)
		return response.data
	}

	async createManualBankWithdraw(data: CreateManualWithdrawTransactionRequest) {
		const response = await axiosInstance.post(
			`/admin/bank-transactions/withdrawals/manual`,
			data,
		)
		return response.data
	}

	async createWithdraw(data: CreateWithdrawTransactionRequest) {
		const response = await axiosInstance.post(
			`/admin/bank-transactions/withdrawals`,
			data,
		)
		return response.data
	}

	async getDepositTransactionsById(id: string) {
		const response =
			await axiosInstance.get<GetDepositTransactionsByIdResponse>(
				`/admin/bank-transactions/deposits/${id}`,
			)
		return response.data
	}

	async getWithdrawalTransactionsById(id: string) {
		const response =
			await axiosInstance.get<GetWithdrawalTransactionsByIdResponse>(
				`/admin/bank-transactions/withdrawals/${id}`,
			)
		return response.data
	}

	async approveWithdraw(id: string, data?: AdminApproveWithdrawRequest) {
		const response = await axiosInstance.patch<void>(
			`/admin/bank-transactions/withdrawals/${id}/approve`,
			data,
		)
		return response.data
	}

	async refundWithdraw(id: string, data?: RefundWithdrawTransactionRequest) {
		const response = await axiosInstance.patch<void>(
			`/admin/bank-transactions/withdrawals/${id}/refund`,
			data,
		)
		return response.data
	}

	async retryWithdraw(id: string) {
		const response = await axiosInstance.patch<void>(
			`/admin/bank-transactions/withdrawals/${id}/retry`,
		)
		return response.data
	}

	async getTrueWalletTransactionLogs(params: GetTrueWalletLogsParams) {
		const response =
			await axiosInstance.get<PaginatedTrueWalletTransactionLogsResponse>(
				'/admin/bank-transactions/true-wallet-logs',
				{ params },
			)
		return response.data
	}

	async getSmsAutoTransactionLogs(params: GetSmsAutoLogsParams) {
		const response =
			await axiosInstance.get<PaginatedSmsAutoTransactionLogsResponse>(
				'/admin/bank-transactions/sms-auto-logs',
				{ params },
			)
		return response.data
	}

	async getTrueMoneyDeposits(params?: GetTrueMoneyDepositsParams) {
		const response = await axiosInstance.get<TrueMoneyDepositsResponse>(
			'/admin/bank-transactions/deposits/truemoney',
			{ params },
		)
		return response.data
	}

	async getTransactionStatistics(params?: GetTransactionStatisticsParams) {
		const response = await axiosInstance.get<TransactionStatisticsResponse>(
			'/admin/bank-transactions/statistics',
			{ params },
		)
		return response.data
	}

	async updateDepositStatus(
		id: string,
		data: { status: string; note?: string },
	) {
		const response = await axiosInstance.patch<{
			success: boolean
			message: string
		}>(`/admin/bank-transactions/deposits/${id}/status`, data)
		return response.data
	}

	async updateWithdrawStatus(
		id: string,
		data: { status: string; note?: string },
	) {
		const response = await axiosInstance.patch<{
			success: boolean
			message: string
		}>(`/admin/bank-transactions/withdrawals/${id}/status`, data)
		return response.data
	}

	async rematchSmsAutoLog(id: string, data: RematchSmsAutoLogRequest) {
		const response = await axiosInstance.post<SmsAutoTransactionLogResponse>(
			`/admin/bank-transactions/sms-auto-logs/${id}/rematch`,
			data,
		)
		return response.data
	}

	async cancelSmsAutoLog(id: string, data: CancelSmsAutoLogRequest) {
		const response = await axiosInstance.post<SmsAutoTransactionLogResponse>(
			`/admin/bank-transactions/sms-auto-logs/${id}/cancel`,
			data,
		)
		return response.data
	}

	async searchUsersForRematch(params: SearchUsersForRematchParams) {
		const response = await axiosInstance.get<SearchUsersForRematchResponse>(
			'/admin/bank-transactions/sms-auto-logs/search-users',
			{ params },
		)
		return response.data
	}

	async updateSmsAutoLogStatus(id: string, data: { status: string }) {
		const response = await axiosInstance.patch(
			`/admin/bank-transactions/sms-auto-logs/${id}/status`,
			data,
		)
		return response.data
	}

	async createDepositForRematch(smsLogId: string, playerId: string) {
		const response = await axiosInstance.post<{
			depositTransactionId: string
			amount: number
			status: string
		}>(`/admin/bank-transactions/sms-auto-logs/${smsLogId}/deposits`, {
			playerId,
		})
		return response.data
	}

	async completeRematchSmsAutoLog(
		id: string,
		data: {
			depositTransactionId?: string
			playerId: string
			newAmount: number
			note?: string
		},
	) {
		const response = await axiosInstance.post(
			`/admin/bank-transactions/sms-auto-logs/${id}/complete-rematch`,
			data,
		)
		return response.data
	}
}

export default new BankTransactionService()
